import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/apiFetch';
import { useApiRoot } from '../contexts/ApiRootContext';

export interface FunctionClassificationDomain {
    domain_name: string;
    domain_key: string;
    domain_description: string;
}

export interface FunctionClassificationSubdomain {
    subdomain_name: string;
    subdomain_key: string;
}

export interface FunctionClassificationFunction {
    function_name: string;
    function_key: string;
}

export interface FunctionClassificationEntry {
    classification_id: string;
    domain: FunctionClassificationDomain;
    subdomain: FunctionClassificationSubdomain;
    function: FunctionClassificationFunction;
}

export interface FunctionEntry {
    classification_id: string;
    name: string;
    key: string;
}

export interface FunctionSubdomain {
    name: string;
    key: string;
    functions: FunctionEntry[];
}

export interface FunctionClassificationHierarchy {
    [domainKey: string]: {
        domain: string;
        description: string;
        subdomains: FunctionSubdomain[];
    };
}

let cachedHref: string | null = null;
let cachedHierarchy: FunctionClassificationHierarchy | null = null;
let inflightHref: string | null = null;
let inflightPromise: Promise<FunctionClassificationHierarchy> | null = null;

function buildHierarchy(entries: FunctionClassificationEntry[]): FunctionClassificationHierarchy {
    const grouped: FunctionClassificationHierarchy = {};
    for (const entry of entries) {
        const dk = entry.domain.domain_key;
        const sk = entry.subdomain.subdomain_key;
        const fk = entry.function.function_key;

        if (!grouped[dk]) {
            grouped[dk] = { domain: entry.domain.domain_name, description: entry.domain.domain_description, subdomains: [] };
        }
        let subdomain = grouped[dk].subdomains.find(s => s.key === sk);
        if (sk && !subdomain) {
            subdomain = { name: entry.subdomain.subdomain_name, key: sk, functions: [] };
            grouped[dk].subdomains.push(subdomain);
        }
        if (subdomain && fk) {
            const existingFn = subdomain.functions.find(f => f.key === fk);
            if (!existingFn) {
                subdomain.functions.push({ classification_id: entry.classification_id, name: entry.function.function_name, key: fk });
            }
        }
    }
    return grouped;
}

function fetchHierarchy(href: string): Promise<FunctionClassificationHierarchy> {
    if (cachedHref === href && cachedHierarchy) {
        return Promise.resolve(cachedHierarchy);
    }
    if (inflightHref === href && inflightPromise) {
        return inflightPromise;
    }

    inflightHref = href;
    inflightPromise = apiFetch(href)
        .then(response => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
        })
        .then((data: FunctionClassificationEntry[]) => {
            const grouped = buildHierarchy(data);
            cachedHref = href;
            cachedHierarchy = grouped;
            return grouped;
        })
        .catch(err => {
            if (inflightHref === href) {
                inflightHref = null;
                inflightPromise = null;
            }
            throw err;
        });

    return inflightPromise;
}

export function useFunctionClassifications() {
    const { loading: rootLoading, getHref } = useApiRoot();
    const href = getHref('function_classifications');
    const [hierarchy, setHierarchy] = useState<FunctionClassificationHierarchy>(
        href && cachedHref === href && cachedHierarchy ? cachedHierarchy : {},
    );
    const [loading, setLoading] = useState(Boolean(rootLoading || href));
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (rootLoading) {
            setLoading(true);
            return;
        }

        if (!href) {
            setHierarchy({});
            setLoading(false);
            setError(null);
            return;
        }

        if (cachedHref === href && cachedHierarchy) {
            setHierarchy(cachedHierarchy);
            setLoading(false);
            setError(null);
            return;
        }

        let cancelled = false;
        setLoading(true);

        fetchHierarchy(href)
            .then(grouped => {
                if (!cancelled) {
                    setHierarchy(grouped);
                    setLoading(false);
                    setError(null);
                }
            })
            .catch(err => {
                console.error('Failed to fetch function classifications:', err);
                if (!cancelled) {
                    setError('Failed to load function classifications');
                    setLoading(false);
                }
            });

        return () => { cancelled = true; };
    }, [rootLoading, href]);

    return { hierarchy, loading, error };
}
